import { Observable } from 'rxjs';

import { Injectable } from '@angular/core';

import { FsMapMarkersServiceProvider } from '@lib/geo/core';
import { SnLonLat } from '@lib/geo/helpers';
import { MapMarkerIcon } from '@lib/geo/map/core/marker/icon-marker-options';
import { IMapMarkerStyleOptions } from '@lib/geo/map/core/marker/marker-options';
import { FsMapPopup } from '@lib/geo/map/core/popup';
import {
  IMarkerAddEvent,
  IMarkerClickEvent,
  IMarkerDestroyEvent,
  IMarkerDoubleClickEvent,
  IMarkerDragEndEvent,
  IMarkerDragMoveEvent,
  IMarkerDragStartEvent,
  IMarkerEventUnion,
  IMarkerIconChangeEvent,
  IMarkerMouseEnterEvent,
  IMarkerMouseLeaveEvent,
  IMarkerMouseOutEvent,
  IMarkerMouseOverEvent,
  IMarkerPositionChangeEvent,
  IMarkerRemoveEvent,
  IMarkerRightClickEvent,
  IMarkerSelectionChangeEvent,
  IMarkerStyleChangeEvent,
  IMarkerTitleChangeEvent,
  IMarkerVisibilityChangeEvent,
} from '@lib/geo/map/interfaces/markers/marker-events.interface';
import { IMarkerOptions } from '@lib/geo/map/interfaces/markers/marker-options.interface';
import { IMarker as _IMarker } from '@lib/geo/map/interfaces/markers/marker.interface';
import { SnBadgeConfig } from '@lib/ui/badge';

import { EsriMap } from '../map/esri-map';
import { EsriMarkerAdapter } from './esri-marker-adapter';

type IMarker = _IMarker<EsriMap>;

@Injectable()
export class EsriMapMarkersService extends FsMapMarkersServiceProvider<EsriMap> {
  public readonly geospatialProvider = 'esri';
  public readonly id = 'esri-map-markers-service';

  private _map: EsriMap | null = null;

  constructor(private readonly _adapter: EsriMarkerAdapter) {
    super();
  }

  public setMap(map: EsriMap): void {
    this._map = map;

    this._adapter.setMap(map);
  }

  // Core marker operations
  public addMarker(marker: IMarkerOptions): IMarker {
    return this._adapter.addMarker(marker);
  }

  public removeMarker(marker: IMarker): void {
    this._adapter.removeMarker(marker);
  }

  public updateMarker(marker: IMarker, options: Partial<IMarkerOptions>): void {
    this._adapter.updateMarker(marker, options);
  }

  // Marker lifecycle and state management
  public setVisible(marker: IMarker, visible: boolean): void {
    this._adapter.setVisible(marker, visible);
  }

  public setPosition(marker: IMarker, position: SnLonLat): void {
    this._adapter.setPosition(marker, position);
  }

  public setIcon(marker: IMarker, icon: MapMarkerIcon): void {
    this._adapter.setIcon(marker, icon);
  }

  public setStyle(marker: IMarker, style: IMapMarkerStyleOptions): void {
    this._adapter.setStyle(marker, style);
  }

  public setDraggable(marker: IMarker, draggable: boolean): void {
    this._adapter.setDraggable(marker, draggable);
  }

  public setRotation(marker: IMarker, rotation: number): void {
    this._adapter.setRotation(marker, rotation);
  }

  public setTitle(marker: IMarker, title: string): void {
    this._adapter.setTitle(marker, title);
  }

  // Marker properties and data access
  public getMarkerId(marker: IMarker): string {
    return this._adapter.getMarkerId(marker);
  }

  public getMarkerPosition(marker: IMarker): SnLonLat {
    return this._adapter.getMarkerPosition(marker);
  }

  public getMarkerTitle(marker: IMarker): string {
    return this._adapter.getMarkerTitle(marker);
  }

  public getMarkerAttributes(marker: IMarker): Record<string, unknown> {
    return this._adapter.getMarkerAttributes(marker);
  }

  public getMarkerCenter(marker: IMarker): { center: number[] } {
    return this._adapter.getMarkerCenter(marker);
  }

  // Badge management
  public setBadge(marker: IMarker, badge: SnBadgeConfig): void {
    this._adapter.setBadge(marker, badge);
  }

  public getBadge(marker: IMarker): SnBadgeConfig {
    return this._adapter.getBadge(marker);
  }

  public createBadge(marker: IMarker, badgeClass?: string): void {
    this._adapter.createBadge(marker, badgeClass);
  }

  public createSelectedBadge(marker: IMarker): void {
    this._adapter.createSelectedBadge(marker);
  }

  public removeSelectedBadge(marker: IMarker): void {
    this._adapter.removeSelectedBadge(marker);
  }

  public setBadgeStatus(
    marker: IMarker,
    status: string,
    badgePosition: Record<string, unknown>,
  ): void {
    this._adapter.setBadgeStatus(marker, status, badgePosition);
  }

  // Popup management
  public setPopup(marker: IMarker, popup: FsMapPopup<EsriMap>): void {
    this._adapter.setPopup(marker, popup);
  }

  // Event handling - Mouse and Touch Events
  public onMarkerClick(marker: IMarker): Observable<IMarkerClickEvent<EsriMap>> {
    return this._adapter.onMarkerClick(marker);
  }

  public onMarkerDoubleClick(marker: IMarker): Observable<IMarkerDoubleClickEvent<EsriMap>> {
    return this._adapter.onMarkerDoubleClick(marker);
  }

  public onMarkerRightClick(marker: IMarker): Observable<IMarkerRightClickEvent<EsriMap>> {
    return this._adapter.onMarkerRightClick(marker);
  }

  public onMarkerMouseOver(marker: IMarker): Observable<IMarkerMouseOverEvent<EsriMap>> {
    return this._adapter.onMarkerMouseOver(marker);
  }

  public onMarkerMouseOut(marker: IMarker): Observable<IMarkerMouseOutEvent<EsriMap>> {
    return this._adapter.onMarkerMouseOut(marker);
  }

  public onMarkerMouseEnter(marker: IMarker): Observable<IMarkerMouseEnterEvent<EsriMap>> {
    return this._adapter.onMarkerMouseEnter(marker);
  }

  public onMarkerMouseLeave(marker: IMarker): Observable<IMarkerMouseLeaveEvent<EsriMap>> {
    return this._adapter.onMarkerMouseLeave(marker);
  }

  // Event handling - Drag Events
  public onMarkerDragStart(marker: IMarker): Observable<IMarkerDragStartEvent<EsriMap>> {
    return this._adapter.onMarkerDragStart(marker);
  }

  public onMarkerDrag(marker: IMarker): Observable<IMarkerDragMoveEvent<EsriMap>> {
    return this._adapter.onMarkerDrag(marker);
  }

  public onMarkerDragEnd(marker: IMarker): Observable<IMarkerDragEndEvent<EsriMap>> {
    return this._adapter.onMarkerDragEnd(marker);
  }

  // Event handling - State Change Events
  public onMarkerPositionChange(marker: IMarker): Observable<IMarkerPositionChangeEvent<EsriMap>> {
    return this._adapter.onMarkerPositionChange(marker);
  }

  public onMarkerTitleChange(marker: IMarker): Observable<IMarkerTitleChangeEvent<EsriMap>> {
    return this._adapter.onMarkerTitleChange(marker);
  }

  public onMarkerVisibilityChange(
    marker: IMarker,
  ): Observable<IMarkerVisibilityChangeEvent<EsriMap>> {
    return this._adapter.onMarkerVisibilityChange(marker);
  }

  public onMarkerSelectionChange(
    marker: IMarker,
  ): Observable<IMarkerSelectionChangeEvent<EsriMap>> {
    return this._adapter.onMarkerSelectionChange(marker);
  }

  public onMarkerIconChange(marker: IMarker): Observable<IMarkerIconChangeEvent<EsriMap>> {
    return this._adapter.onMarkerIconChange(marker);
  }

  public onMarkerStyleChange(marker: IMarker): Observable<IMarkerStyleChangeEvent<EsriMap>> {
    return this._adapter.onMarkerStyleChange(marker);
  }

  // Event handling - Lifecycle Events
  public onMarkerAdd(marker: IMarker): Observable<IMarkerAddEvent<EsriMap>> {
    return this._adapter.onMarkerAdd(marker);
  }

  public onMarkerRemove(marker: IMarker): Observable<IMarkerRemoveEvent<EsriMap>> {
    return this._adapter.onMarkerRemove(marker);
  }

  public onMarkerDestroy(marker: IMarker): Observable<IMarkerDestroyEvent<EsriMap>> {
    return this._adapter.onMarkerDestroy(marker);
  }

  // Event handling - All Events (for general event listening)
  public onMarkerEvents(marker: IMarker): Observable<IMarkerEventUnion<EsriMap>> {
    return this._adapter.onMarkerEvents(marker);
  }

  // Marker query and search
  public findMarkerById(id: string): IMarker | null {
    return this._adapter.findMarkerById(id);
  }

  public getAllMarkers(): IMarker[] {
    return this._adapter.getAllMarkers();
  }

  public getMarkersByAttribute(attribute: string, value: unknown): IMarker[] {
    return this._adapter.getMarkersByAttribute(attribute, value);
  }
}
